/**
 * Gamification utility functions.
 *
 * - ISO weekday helpers (JST-aware, matching API `weeklyLoginWeekdays` numbering)
 * - Learner stats merging (profile + SSE)
 */

import { TZDate } from '@date-fns/tz';
import { format, getISODay, setISODay } from 'date-fns';

import type { components } from '~/api/openapi';
import type { GamificationStatsDto } from '~/lib/sse-client';
import { getDateFnsLocale, type SupportedLocale } from '~/lib/utils';

// Any stable date — only the weekday of the result matters, not the calendar date.
const REFERENCE_DATE = new Date(2024, 0, 1);

function clampIsoWeekday(isoWeekday: number): number {
  return Math.min(7, Math.max(1, Math.floor(isoWeekday)));
}

/** ISO weekday 1–7 for the given instant in `timeZone` (default Asia/Tokyo). */
export function getIsoWeekdayInTimeZone(date: Date, timeZone = 'Asia/Tokyo'): number {
  return getISODay(new TZDate(date, timeZone));
}

/** Short weekday label for one ISO weekday (1–7). */
export function getIsoWeekdayShortLabel(
  isoWeekday: number,
  locale: SupportedLocale = 'en'
): string {
  return format(setISODay(REFERENCE_DATE, clampIsoWeekday(isoWeekday)), 'EEE', {
    locale: getDateFnsLocale(locale),
  });
}

export const WEEK_ISO_ORDER = [1, 2, 3, 4, 5, 6, 7] as const;

// Learner stats merging (profile + SSE)
export type MergedLearnerGamificationStats = {
  weeklyLoginDays: number;
  weeklyLoginWeekdays: number[];
  totalXp: number;
  totalStars: number;
  requiredLoginDays: number;
  weeklyBonusXp: number;
};

const DEFAULT_REQUIRED_LOGIN_DAYS = 4;
const DEFAULT_WEEKLY_BONUS_XP = 10;

export function mergeLearnerGamificationStats(
  profile: components['schemas']['GamificationStatsDto'],
  sse: GamificationStatsDto | null | undefined
): MergedLearnerGamificationStats {
  return {
    weeklyLoginDays: sse?.weeklyLoginDays ?? profile.weeklyLoginDays,
    weeklyLoginWeekdays: sse?.weeklyLoginWeekdays ?? profile.weeklyLoginWeekdays,
    totalXp: sse?.totalXp ?? profile.totalXp,
    totalStars: sse?.totalStars ?? profile.totalStars,
    requiredLoginDays:
      sse?.weeklyLoginChallengeConfig?.requiredLoginDays ??
      profile.weeklyLoginChallengeConfig?.requiredLoginDays ??
      DEFAULT_REQUIRED_LOGIN_DAYS,
    weeklyBonusXp:
      sse?.weeklyLoginChallengeConfig?.bonusXp ??
      profile.weeklyLoginChallengeConfig?.bonusXp ??
      DEFAULT_WEEKLY_BONUS_XP,
  };
}
